import { memo, useMemo } from "react";
import { rgbToCss } from "utils/rgbToCss";
import { darkenRgb } from "components/SmartSurveyor/SmartSurveyorV3/utils/floorPlanLayout/darkenRgb";
import { LAYOUT_STYLE_BY_VIEW } from "../constants";
import { ringsToPathData } from "../ringsToPathData";
import type { LayoutPathProps } from "./types";

// No zoom prop on purpose: zooming does not re-render the shapes.
export const LayoutPath = memo(function LayoutPath({
  shape,
  view,
  zoneStrokeWidth,
  isHighlighted,
  onHoverChange,
}: LayoutPathProps) {
  const style = LAYOUT_STYLE_BY_VIEW[view];
  const darkerColor = rgbToCss(darkenRgb({ rgb: shape.rgb }));

  // The rings keep their identity until the layout changes, so the string is built once per shape.
  const pathData = useMemo(() => ringsToPathData(shape.rings), [shape.rings]);

  return (
    // The group and the part names are what the view's greyOutSiblingsClassName selects.
    <g
      data-testid="layout-shape-group"
      data-highlighted={isHighlighted || undefined}
    >
      <path
        data-testid="layout-shape"
        data-shape-id={shape.id}
        data-layout-part="fill"
        d={pathData}
        fill={rgbToCss(shape.rgb)}
        fillOpacity={
          isHighlighted ? style.highlightFillOpacity : style.fillOpacity
        }
        stroke={style.isStrokeDarker ? darkerColor : rgbToCss(shape.rgb)}
        strokeWidth={style.screenStrokeWidth ?? zoneStrokeWidth}
        vectorEffect={
          style.screenStrokeWidth === null ? undefined : "non-scaling-stroke"
        }
        strokeLinejoin="round"
        opacity={isHighlighted ? style.highlightOpacity : style.opacity}
        // The mouse hover look is plain CSS; the plan panel handles clicks itself, so only hover is listened to.
        className={`pointer-events-auto ${style.hoverClassName}`}
        onMouseEnter={(event) => onHoverChange(shape, event)}
        onMouseLeave={(event) => onHoverChange(null, event)}
      />
      {style.borderScreenWidth !== null && (
        <path
          data-testid="layout-shape-border"
          data-layout-part="border"
          d={pathData}
          fill="none"
          stroke={darkerColor}
          strokeWidth={style.borderScreenWidth}
          vectorEffect="non-scaling-stroke"
          strokeDasharray={shape.strokeDash}
          strokeLinejoin="round"
          pointerEvents="none"
        />
      )}
    </g>
  );
});
